import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { format, startOfWeek, addDays } from 'date-fns';
import DailyScheduleView from './DailyScheduleView';
import WeeklyScheduleView from './WeeklyScheduleView';

const ManagerDashboard = () => {
  const { user } = useAuth();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [view, setView] = useState('weekly'); // 'daily' or 'weekly'
  const [schedules, setSchedules] = useState([]);
  const [weekSchedules, setWeekSchedules] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Don't fetch schedules if user doesn't have a gym assigned yet
    if (!user?.gymId) {
      setLoading(false);
      setError(null); // Clear any previous errors
      return;
    }

    if (view === 'daily') {
      fetchGymSchedule();
    } else {
      fetchWeekSchedule();
    }
  }, [selectedDate, view, user?.gymId]);

  const fetchGymSchedule = async () => {
    try {
      setLoading(true);
      setError(null);

      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/reservations/gym-schedule/${user.gymId}?date=${dateStr}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch gym schedule');
      }

      const data = await response.json();
      setSchedules(data);
    } catch (err) {
      console.error('Error fetching gym schedule:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchWeekSchedule = async () => {
    try {
      setLoading(true);
      setError(null);

      const weekStart = startOfWeek(selectedDate, { weekStartsOn: 0 });
      const weekData = {};

      // Fetch data for each day of the week
      const promises = Array.from({ length: 7 }, (_, i) => {
        const day = addDays(weekStart, i);
        const dateStr = format(day, 'yyyy-MM-dd');

        return fetch(
          `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/reservations/gym-schedule/${user.gymId}?date=${dateStr}`,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          }
        ).then(response => {
          if (!response.ok) {
            throw new Error('Failed to fetch gym schedule');
          }
          return response.json();
        }).then(data => {
          weekData[dateStr] = data;
        });
      });

      await Promise.all(promises);
      setWeekSchedules(weekData);
    } catch (err) {
      console.error('Error fetching week schedule:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = (direction) => {
    const newDate = new Date(selectedDate);
    if (view === 'daily') {
      newDate.setDate(newDate.getDate() + direction);
    } else {
      newDate.setDate(newDate.getDate() + (direction * 7)); // Move by weeks
    }
    setSelectedDate(newDate);
  };

  const goToToday = () => {
    setSelectedDate(new Date());
  };

  const handleDayClick = (day) => {
    setSelectedDate(day);
    setView('daily');
  };

  const isToday = format(selectedDate, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');

  // If user doesn't have a gym assigned yet, show onboarding message
  if (!user?.gymId) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-md p-8 text-center max-w-2xl mx-auto">
          <div className="mb-6">
            <svg className="w-20 h-20 mx-auto text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Welcome to BoardRez!</h2>
          <p className="text-lg text-gray-600 mb-6">
            Your gym manager account is almost ready. Please complete your gym setup to access the dashboard.
          </p>
          <p className="text-sm text-gray-500">
            If you've already completed onboarding and are seeing this message, please contact support.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Marketing Materials Quick Link */}
      <div className="bg-gradient-to-r from-teal-500 to-purple-500 rounded-lg shadow-md p-4 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-2xl">📣</span>
            <div>
              <h3 className="text-white font-bold">Promote BoardRez to Your Members</h3>
              <p className="text-white text-sm opacity-90">Get QR codes, social media posts, and more</p>
            </div>
          </div>
          <button
            onClick={() => window.location.href = '/marketing-materials'}
            className="px-4 py-2 bg-white text-teal-600 font-bold rounded-lg hover:bg-gray-50 transition-colors"
          >
            View Materials →
          </button>
        </div>
      </div>

      {/* View Toggle Tabs */}
          <div className="bg-white rounded-lg shadow-md mb-6">
            <div className="border-b border-gray-200">
              <nav className="flex -mb-px">
                <button
                  onClick={() => setView('weekly')}
                  className={`
                    px-6 py-4 text-sm font-medium border-b-2 transition-colors
                    ${view === 'weekly'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }
                  `}
                >
                  Weekly View
                </button>
                <button
                  onClick={() => setView('daily')}
                  className={`
                    px-6 py-4 text-sm font-medium border-b-2 transition-colors
                    ${view === 'daily'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }
                  `}
                >
                  Daily View
                </button>
              </nav>
            </div>
          </div>

          {/* Date Navigation */}
          <div className="bg-white rounded-lg shadow-md p-4 mb-6">
            <div className="flex items-center justify-between">
              <button
                onClick={() => handleDateChange(-1)}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                ← Previous {view === 'daily' ? 'Day' : 'Week'}
              </button>

              <div className="flex items-center gap-4">
                <h2 className="text-xl font-semibold text-gray-900">
                  {view === 'daily'
                    ? format(selectedDate, 'EEEE, MMMM d, yyyy')
                    : `Week of ${format(startOfWeek(selectedDate, { weekStartsOn: 0 }), 'MMMM d, yyyy')}`
                  }
                </h2>
                {!isToday && (
                  <button
                    onClick={goToToday}
                    className="px-3 py-1 text-sm bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                  >
                    Go to Today
                  </button>
                )}
              </div>

              <button
                onClick={() => handleDateChange(1)}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Next {view === 'daily' ? 'Day' : 'Week'} →
              </button>
            </div>
          </div>

          {/* Schedule View */}
          {loading ? (
            <div className="bg-white rounded-lg shadow-md p-8 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading schedule...</p>
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-600">Error: {error}</p>
              <button
                onClick={view === 'daily' ? fetchGymSchedule : fetchWeekSchedule}
                className="mt-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
              >
                Retry
              </button>
            </div>
          ) : view === 'daily' ? (
            schedules.length === 0 ? (
              <div className="bg-white rounded-lg shadow-md p-8 text-center">
                <p className="text-gray-600">No boards found for your gym.</p>
              </div>
            ) : (
              <DailyScheduleView
                schedules={schedules}
                selectedDate={selectedDate}
                onReservationUpdate={fetchGymSchedule}
              />
            )
          ) : (
            <WeeklyScheduleView
              weekSchedules={weekSchedules}
              selectedDate={selectedDate}
              onDayClick={handleDayClick}
            />
          )}
    </div>
  );
};

export default ManagerDashboard;
